import * as z from "zod";
import { type Caller, type CursorPage, consentTextVersion, cursorPagination, type Result, version } from "../../index";

// The campaigns and the invitations side of the funnel (ADR-287 §2, ADR-291): the facts for the projection of crm, in
// ids, and what a person of Tomorrow does on the invitations and the links from Guestlist, through crm. With the
// orders of sales (ADR-290), crm attributes an order to a campaign by customer, brand and season.

/**
 * Where an invitation of a customer to meet a brand in a campaign stands:
 * - `invited`: invited, no appointment with the brand;
 * - `booked`: an appointment with the brand in the campaign, booked or moved;
 * - `visited`: the appointment took place;
 * - `no_show`: the customer did not come;
 * - `declined`: the customer gave the brand up;
 * - `withdrawn`: Tomorrow withdrew the invitation.
 */
export const invitationFunnelStages = ["invited", "booked", "visited", "no_show", "declined", "withdrawn"] as const;
export type InvitationFunnelStage = (typeof invitationFunnelStages)[number];

/**
 * The permission the writes of this part ask of the person crm acts for (ADR-299 §1): only these four operations of the
 * entrypoint crm binds check it, and no route of Backstage, so a role of Guestlist that holds it opens nothing there,
 * though the Actor is the union of the roles (ADR-294 §2).
 */
export const INVITATION_WRITE = "booking.invitation.write";

/**
 * The domain codes the writes of this part answer with, besides the common ones: crm hands them to Guestlist as they
 * are (ADR-299 §2), and the app has a text for each.
 */
export type CampaignFunnelErrorCode =
  | "sales_campaign_archived"
  | "campaign_invitation_invalid"
  | "booking_link_contact_invalid"
  | "booking_link_not_sent";

/** The event that wakes the projection for an invitation, and the field of its id (ADR-290 §9). */
export const INVITATION_CHANGED_EVENT = { type: "booking.invitationChanged.v1", id: "invitationId" } as const;

/**
 * The event that wakes the projection for the funnel of a personal link (ADR-313 §4), and the field of its id: the
 * customer opened the page of the link, saw the times of a day or booked.
 */
export const LINK_ENGAGED_EVENT = { type: "booking.linkEngaged.v1", id: "linkId" } as const;

/**
 * The fact that a contact gave the consent to the measure of opens on the page of its booking link (ADR-314 §4): crm
 * writes it as the proof `booking_page`. The fact is the whole of it, so crm reads nothing again; `consentId` is the id
 * of the proof, the same at every delivery. booking keeps its event equal to this shape with a test.
 */
export const OPEN_TRACKING_GIVEN_EVENT = { type: "booking.openTrackingGiven.v1" } as const;
export const openTrackingGivenFact = z.object({
  consentId: z.uuid(),
  /** The link the box was ticked on, the aggregate of booking's event. */
  linkId: z.uuid(),
  contactPersonId: z.uuid(),
  givenAt: z.iso.datetime(),
  /** Any version booking served: crm keeps it, and does not need to know booking's list (ADR-084 point 4). */
  textVersion: consentTextVersion,
});

/**
 * How far the customer of a personal link went, in order (booking keeps them equal to its own with a test):
 * - `opened`: the page of the link opened;
 * - `slots_viewed`: the times of a day seen;
 * - `booked`: an appointment booked from it.
 */
export const linkFunnelSteps = ["opened", "slots_viewed", "booked"] as const;
export type LinkFunnelStep = (typeof linkFunnelSteps)[number];

/**
 * Who holds a personal link: a contact of the customer, to whom Tomorrow emailed or handed it (`contact`), or a user of
 * the customer, who opened the booking in Frontrow (`user`).
 */
export const linkHolders = ["contact", "user"] as const;
export type LinkHolder = (typeof linkHolders)[number];

/** The cursor of the facts: the id of the last item of the page. */
export const campaignFunnelCursor = z.tuple([z.uuid()]);

/**
 * The campaigns, by id: all of them for a full realignment, page after page; those an invitation names
 * (`campaignIds`); or those of a season.
 */
export const listCampaignFactsInput = z.object({
  campaignIds: z.array(z.uuid()).min(1).max(100).optional(),
  seasonId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 500, maxLimit: 1000 }),
});
export type ListCampaignFactsInput = z.input<typeof listCampaignFactsInput>;

const campaignLinkUrl = z.url({ protocol: /^https$/ }).max(2000);

/** A sales campaign: what it is for, where and when. Its name is how users know it, as the number of an order. */
export const campaignFactDto = z
  .object({
    campaignId: z.uuid(),
    name: z.string(),
    seasonId: z.uuid().nullable(),
    categoryId: z.uuid().nullable(),
    showroomLocationId: z.uuid().nullable(),
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    /**
     * The Portfolio and the Flyer of the campaign (ADR-312), the links the emails of Guestlist can use: optional, since
     * a source without them answers neither; only https, as the projection keeps them.
     */
    portfolioUrl: campaignLinkUrl.nullable().optional(),
    flyerUrl: campaignLinkUrl.nullable().optional(),
    changedAt: z.iso.datetime(),
  })
  .meta({ id: "CampaignFact" });
export type CampaignFact = z.output<typeof campaignFactDto>;

/**
 * The invitations, by id: every one for a full realignment, page after page; those of an event (`invitationIds`) to
 * read them again; or those of a campaign, a customer or a brand.
 */
export const listInvitationFunnelFactsInput = z.object({
  invitationIds: z.array(z.uuid()).min(1).max(100).optional(),
  campaignId: z.uuid().optional(),
  customerOrgId: z.uuid().optional(),
  brandOrgId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 500, maxLimit: 1000 }),
});
export type ListInvitationFunnelFactsInput = z.input<typeof listInvitationFunnelFactsInput>;

/** An invitation of a customer to meet a brand in a campaign. */
export const invitationFunnelFactDto = z
  .object({
    invitationId: z.uuid(),
    campaignId: z.uuid(),
    customerOrgId: z.uuid(),
    brandOrgId: z.uuid(),
    stage: z.enum(invitationFunnelStages),
    /** Null for an invitation born from an appointment nobody invited to. */
    invitedAt: z.iso.datetime().nullable(),
    /** The appointment that last moved it, and when it starts: null until there is one. */
    appointmentId: z.uuid().nullable(),
    appointmentStartsAt: z.iso.datetime().nullable(),
    stageChangedAt: z.iso.datetime(),
    changedAt: z.iso.datetime(),
    /** What a decision on it sends back. */
    version: z.number().int(),
  })
  .meta({ id: "InvitationFunnelFact" });
export type InvitationFunnelFact = z.output<typeof invitationFunnelFactDto>;

/**
 * The funnels of the personal links a customer opened, by id: every one for a full realignment, page after page; those
 * of an event (`linkIds`) to read them again; or those of a campaign or of a customer. A link nobody opened has none.
 */
export const listLinkFactsInput = z.object({
  linkIds: z.array(z.uuid()).min(1).max(100).optional(),
  campaignId: z.uuid().optional(),
  customerOrgId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 500, maxLimit: 1000 }),
});
export type ListLinkFactsInput = z.input<typeof listLinkFactsInput>;

/**
 * How far the customer of a personal link went (ADR-313 §4). A session is a visit more than 30 minutes after the start
 * of the one before; the first and the last of each step are the starts of its first and of its last session.
 */
export const linkFunnelFactDto = z
  .object({
    linkId: z.uuid(),
    campaignId: z.uuid(),
    customerOrgId: z.uuid(),
    /** The contact of the link; for the link of a user, the contact linked to the user, if any. */
    contactPersonId: z.uuid().nullable(),
    holder: z.enum(linkHolders),
    firstOpenedAt: z.iso.datetime().nullable(),
    lastOpenedAt: z.iso.datetime().nullable(),
    openSessions: z.number().int().min(0),
    firstSlotsViewedAt: z.iso.datetime().nullable(),
    lastSlotsViewedAt: z.iso.datetime().nullable(),
    slotSessions: z.number().int().min(0),
    /** The brands whose times it saw. */
    viewedBrandOrgIds: z.array(z.uuid()).max(1000),
    /** Its first appointment booked from the link. */
    bookedAt: z.iso.datetime().nullable(),
    furthestStep: z.enum(linkFunnelSteps),
    changedAt: z.iso.datetime(),
  })
  .meta({ id: "LinkFunnelFact" });
export type LinkFunnelFact = z.output<typeof linkFunnelFactDto>;

/**
 * Invites customers to meet brands of a campaign: every customer to every brand. Again for an invitation already there
 * changes nothing, unless it was withdrawn (an invitation again) or born from an appointment nobody invited to (now
 * invited too).
 */
export const inviteToCampaignInput = z.object({
  campaignId: z.uuid(),
  customerOrgIds: z.array(z.uuid()).min(1).max(200),
  brandOrgIds: z.array(z.uuid()).min(1).max(50),
});
export type InviteToCampaignInput = z.input<typeof inviteToCampaignInput>;

export const campaignInvitationsDto = z
  .object({
    /** The invitations born, withdrawn and invited again, or born from an appointment and now invited. */
    invited: z.number().int(),
    /** Those already there, left as they were. */
    unchanged: z.number().int(),
  })
  .meta({ id: "CampaignInvitations" });
export type CampaignInvitations = z.output<typeof campaignInvitationsDto>;

/**
 * What Tomorrow decides of an invitation with no appointment: the customer gave the brand up (`decline`), Tomorrow
 * withdraws it (`withdraw`), or either is undone (`reopen`). An appointment decides the rest.
 */
export const invitationDecisions = ["decline", "withdraw", "reopen"] as const;
export type InvitationDecision = (typeof invitationDecisions)[number];

/**
 * The decisions an invitation at a stage takes, as booking's machines allow them (a test of booking keeps them equal):
 * what the matrix of Guestlist offers. An appointment decides a booked, visited or no-show invitation.
 */
export const decisionsAt: Record<InvitationFunnelStage, readonly InvitationDecision[]> = {
  invited: ["decline", "withdraw"],
  booked: [],
  visited: [],
  no_show: [],
  declined: ["withdraw", "reopen"],
  withdrawn: ["reopen"],
};

export const decideCampaignInvitationInput = z.object({
  invitationId: z.uuid(),
  version,
  decision: z.enum(invitationDecisions),
});
export type DecideCampaignInvitationInput = z.input<typeof decideCampaignInvitationInput>;

/** Why a contact gets no link: not a contact of a customer, archived, or without an email. */
export const linkRecipientRules = ["unknown_reference", "no_email"] as const;
export type LinkRecipientRule = (typeof linkRecipientRules)[number];

/**
 * The link of the seller (B-145): the personal link of a contact of a customer for a campaign, handed to the person of
 * Tomorrow who asks, to send it its own way. It replaces the contact's link before, as an emailed one does.
 */
export const issueSellerBookingLinkInput = z.object({
  campaignId: z.uuid(),
  contactPersonId: z.uuid(),
});
export type IssueSellerBookingLinkInput = z.input<typeof issueSellerBookingLinkInput>;

/** The link, once: booking keeps only the SHA-256 of its token. */
export const sellerBookingLinkDto = z
  .object({
    url: z.url(),
    expiresAt: z.iso.datetime(),
  })
  .meta({ id: "SellerBookingLink" });
export type SellerBookingLink = z.output<typeof sellerBookingLinkDto>;

/**
 * The permission of the system Actor of the steps of a send of crm (ADR-319 §6), which no role has: the personal links
 * of its recipients, issued by booking as it issues the link of the seller.
 */
export const NEXXT_SOURCE_LINKS = "nexxt_source.links.issue";

/**
 * The personal links of some contacts of the customers of a campaign, for the booking button of a send of crm (ADR-313
 * §5, ADR-319 §6): each replaces the contact's link before, as an emailed one does, and works from now. A contact that
 * cannot have one is said, the others are issued.
 */
export const issueCampaignBookingLinksInput = z.object({
  campaignId: z.uuid(),
  contactPersonIds: z.array(z.uuid()).min(1).max(100),
});
export type IssueCampaignBookingLinksInput = z.input<typeof issueCampaignBookingLinksInput>;

/**
 * The links, once, with their id: crm keeps the id on the recipient (`booking_link_id`) and puts the address in the
 * email alone; booking keeps only the SHA-256 of the token.
 */
export const campaignBookingLinksIssuedDto = z
  .object({
    links: z.array(
      z.object({ contactPersonId: z.uuid(), linkId: z.uuid(), url: z.url(), expiresAt: z.iso.datetime() }),
    ),
    skipped: z.array(z.object({ contactPersonId: z.uuid(), rule: z.enum(linkRecipientRules) })),
  })
  .meta({ id: "CampaignBookingLinksIssued" });
export type CampaignBookingLinksIssued = z.output<typeof campaignBookingLinksIssuedDto>;

/**
 * What the domain of the campaigns serves to crm, on the entrypoint crm binds: the facts to the system Actor of crm,
 * the rest to the person of Tomorrow crm acts for.
 */
export interface CampaignFunnelSource {
  listCampaignFacts(caller: Caller, input: ListCampaignFactsInput): Promise<Result<CursorPage<CampaignFact>>>;
  listInvitationFunnelFacts(
    caller: Caller,
    input: ListInvitationFunnelFactsInput,
  ): Promise<Result<CursorPage<InvitationFunnelFact>>>;
  listLinkFacts(caller: Caller, input: ListLinkFactsInput): Promise<Result<CursorPage<LinkFunnelFact>>>;
  inviteToCampaign(caller: Caller, input: InviteToCampaignInput): Promise<Result<CampaignInvitations>>;
  decideCampaignInvitation(caller: Caller, input: DecideCampaignInvitationInput): Promise<Result<InvitationFunnelFact>>;
  issueSellerBookingLink(caller: Caller, input: IssueSellerBookingLinkInput): Promise<Result<SellerBookingLink>>;
  issueCampaignBookingLinks(
    caller: Caller,
    input: IssueCampaignBookingLinksInput,
  ): Promise<Result<CampaignBookingLinksIssued>>;
}
